import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ADMIN, USER_ROLE_ANONYMOUS } from 'common/userRoles';

import {
  PARTNER_TASK_STATUS_NEEDS_REVIEW,
  PARTNER_TASK_STATUS_SENT,
  PARTNER_TASK_STATUS_IN_PROGRESS,
  PARTNER_TASK_STATUS_COMPLETED,
  PARTNER_TASK_STATUS_CANCELLED,
  PARTNER_TASK_CATEGORY_CLEAN,
  PARTNER_TASK_CATEGORY_LAWN,
  PARTNER_TASK_CATEGORY_FIX,
  PARTNER_TASK_CATEGORY_PRICING,
} from 'common/partnerTaskConstants';

import { formatPrice, formatAgoDate, formatPercentage } from 'common/formatters';

import { getListingFullAddressString } from 'common/listingHelpers';

import {
  SR_API_ACTION_PARTNER_TASK_REDUCE_PRICE,
  SR_API_ACTION_PARTNER_TASK_ARCHIVE,
} from 'api/actionTypes';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

import ButtonDeprecated, {
  BUTTON_THEME_GREEN_ADOBE,
  BUTTON_THEME_GREY_ADOBE,
} from 'client/components/ButtonDeprecated';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const taskCategorySettings = {
  [PARTNER_TASK_CATEGORY_CLEAN]: {
    rowColorHex: '#FDF4ED',
    icon: 'partner-task-dirty',
    label: 'Dirty',
    titleColor: 'orange300',
  },
  [PARTNER_TASK_CATEGORY_LAWN]: {
    rowColorHex: '#FDF4ED',
    icon: 'partner-task-yard',
    label: 'Unkept Yard',
    titleColor: 'orange300',
  },
  [PARTNER_TASK_CATEGORY_FIX]: {
    rowColorHex: '#FDF0F1',
    icon: 'partner-task-broken',
    label: 'Broken',
    titleColor: 'red',
  },
  [PARTNER_TASK_CATEGORY_PRICING]: {
    rowColorHex: '#FDF0F1',
    icon: 'partner-task-cold',
    label: 'Cold Listing',
    titleColor: 'red',
  },
};

const getTaskCategorySettings = ({ task }) => {
  if (task.category === PARTNER_TASK_CATEGORY_PRICING && task.keyword === 'flatLined') {
    return {
      rowColorHex: '#FDF0F1',
      icon: 'partner-task-flat-lined',
      label: 'Flatlined Listing',
      titleColor: 'red',
    };
  }

  return taskCategorySettings[task.category];
};

const getArchiveTaskFormFields = ({ task }) => [
  {
    id: 'archiveReason',
    title: 'Archive Reason?',
    subtitle:
      'Write the reason so we can understand the decision to not ' +
      (task.type === PARTNER_TASK_CATEGORY_PRICING ? 'lower the price' : 'fix the issue'),
    type: 'long_text',
    validations: ['required'],
    properties: {
      placeholder: 'Write here...',
      noMargin: true,
    },
  },
];

const getTaskWeeksAgo = ({ createdAt, addWeeks }) =>
  moment().diff(moment(createdAt).subtract(addWeeks || 0, 'weeks'), 'weeks');

const getReviewFeedbackText = (review) =>
  lodashCompact([
    review.feedback &&
      `${review.feedback}${review.feedback[review.feedback.length - 1] === '.' ? '' : '.'}`,
    Array.isArray(review.tags) && review.tags.length > 0
      ? `Tags: ${review.tags.join(', ')}.`
      : null,
    review.dirtyExplanation && `What renter said was dirty: ${review.dirtyExplanation}`,
  ]).join(' ');

const SrPartnerTaskRowInfoLabel = ({ task }) => {
  let taskInfoLabel;

  const taskCreatedTimestamp = moment(task.createdAt).format('MMM D');

  if (task.reviews.length > 0) {
    taskInfoLabel = `(${pluralize('Tour', task.tourCount, true)})`;
  }

  if (task.category === PARTNER_TASK_CATEGORY_LAWN) {
    const weeksAgo = getTaskWeeksAgo({ createdAt: task.createdAt, addWeeks: 2 });
    taskInfoLabel = `(unkept for ${pluralize('week', weeksAgo, true)})`;
  }

  taskInfoLabel = `${taskCreatedTimestamp} ${taskInfoLabel || ''}`.trim();

  return !taskInfoLabel ? null : (
    <SrText font="greycliff" weight="800" size="14" color="greyMonochrome">
      {taskInfoLabel}
    </SrText>
  );
};

SrPartnerTaskRowInfoLabel.displayName = 'SrPartnerTaskRowInfoLabel';

SrPartnerTaskRowInfoLabel.propTypes = {
  task: PropTypes.object.isRequired,
};

const SrTaskListRowAction = ({ task, updatePartnerTask, onReducePrice }) => {
  let actionDOM = null;

  if (task.status === PARTNER_TASK_STATUS_SENT && task.category !== PARTNER_TASK_CATEGORY_PRICING) {
    actionDOM = (
      <SrText
        font="greycliff"
        cursor="pointer"
        weight="700"
        size="16"
        color="blue300"
        decoration="underline"
        onClick={() =>
          updatePartnerTask({
            partnerTaskId: task.id,
            updates: {
              status: PARTNER_TASK_STATUS_IN_PROGRESS,
            },
          })
        }
      >
        Start Fixing
      </SrText>
    );
  }

  if (
    task.category === PARTNER_TASK_CATEGORY_PRICING &&
    task.priceDropRecommendation &&
    onReducePrice &&
    (task.status === PARTNER_TASK_STATUS_SENT || task.status === PARTNER_TASK_STATUS_IN_PROGRESS)
  ) {
    actionDOM = (
      <SrText
        font="greycliff"
        cursor="pointer"
        weight="700"
        size="16"
        color="blue300"
        decoration="underline"
        onClick={onReducePrice}
      >
        Lower Price
      </SrText>
    );
  }

  if (
    task.status === PARTNER_TASK_STATUS_IN_PROGRESS &&
    task.category !== PARTNER_TASK_CATEGORY_PRICING
  ) {
    actionDOM = (
      <SrText
        font="greycliff"
        cursor="pointer"
        weight="700"
        size="16"
        color="green"
        decoration="underline"
        onClick={() =>
          updatePartnerTask({
            partnerTaskId: task.id,
            updates: {
              status: PARTNER_TASK_STATUS_COMPLETED,
            },
          })
        }
      >
        Mark Done
      </SrText>
    );
  }

  return !actionDOM ? null : (
    <SrBox width="115px" flexShrink="0" alignSelf="center" textAlign="right">
      {actionDOM}
    </SrBox>
  );
};

SrTaskListRowAction.displayName = 'SrTaskListRowAction';

SrTaskListRowAction.propTypes = {
  task: PropTypes.object.isRequired,
  updatePartnerTask: PropTypes.func.isRequired,
};

const SrPartnerTaskRowTitle = ({ task }) => {
  const categorySettings = getTaskCategorySettings({ task });

  return (
    <>
      <SrBox display="inlineBlock" position="relative" top="2px">
        <SrIcon className={categorySettings.icon} display="inlineBlock" />
      </SrBox>
      <SrText
        font="greycliff"
        weight="800"
        size="14"
        color={categorySettings.titleColor}
        padding="0 4px"
      >
        {categorySettings.label}
      </SrText>
    </>
  );
};

SrPartnerTaskRowTitle.displayName = 'SrPartnerTaskRowTitle';

SrPartnerTaskRowTitle.propTypes = {
  task: PropTypes.object.isRequired,
};

const SrPartnerTaskListRow = ({
  task,
  rowColorHex,
  borderColorHex,
  currentUser,
  updatePartnerTask,
  showListingAddress,
}) => {
  const dispatch = useDispatch();
  const [priceReductionModalOpen, setPriceReductionModalOpen] = useState(false);
  const [archiveTaskModalOpen, setArchiveTaskModalOpen] = useState(false);

  const categorySettings = getTaskCategorySettings({ task });
  const backgroundColorHex = rowColorHex || categorySettings.rowColorHex;
  const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);
  const isAnonymous = currentUser.roles.includes(USER_ROLE_ANONYMOUS);
  const isPriceReductionTask = task.category === PARTNER_TASK_CATEGORY_PRICING;

  const archiveTaskFormFields = getArchiveTaskFormFields({ task });

  const onReducePrice = useCallback(() => {
    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_TASK_REDUCE_PRICE,
      payload: {
        partnerTaskId: task.id,
        price: task.priceDropRecommendation,
      },
    }).then(() => {
      updatePartnerTask();
    });
  }, [dispatch, updatePartnerTask, task]);

  const onArchiveTask = useCallback(
    ({ archiveReason }) => {
      dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_TASK_ARCHIVE,
        payload: {
          partnerTaskId: task.id,
          archiveReason,
        },
      }).then(() => {
        updatePartnerTask();
      });
    },
    [dispatch, updatePartnerTask, task],
  );

  return (
    <div
      style={{
        border: borderColorHex ? `1px solid ${borderColorHex}` : null,
        borderRadius: '7px',
        padding: '16px',
        backgroundColor: backgroundColorHex,
      }}
    >
      <SrFlex justify="between">
        <div>
          <SrPartnerTaskRowTitle task={task} />
          <SrPartnerTaskRowInfoLabel task={task} />
          {isAdmin && task.status === PARTNER_TASK_STATUS_NEEDS_REVIEW && (
            <SrText
              font="greycliff"
              cursor="pointer"
              weight="700"
              color="blue300"
              size="14"
              decoration="underline"
              paddingLeft="10px"
              onClick={() =>
                updatePartnerTask({
                  partnerTaskId: task.id,
                  updates: {
                    status: PARTNER_TASK_STATUS_SENT,
                  },
                })
              }
            >
              Show to Partner
            </SrText>
          )}
          {isAdmin && task.status === PARTNER_TASK_STATUS_NEEDS_REVIEW && (
            <SrText
              font="greycliff"
              cursor="pointer"
              weight="700"
              color="greyMonochrome"
              size="14"
              decoration="underline"
              paddingLeft="10px"
              onClick={() =>
                updatePartnerTask({
                  partnerTaskId: task.id,
                  updates: {
                    status: PARTNER_TASK_STATUS_CANCELLED,
                  },
                })
              }
            >
              Do Not Show
            </SrText>
          )}
          {!isAnonymous && task.status === PARTNER_TASK_STATUS_IN_PROGRESS && (
            <SrText
              font="greycliff"
              cursor="pointer"
              weight="700"
              color="greyMonochrome"
              size="14"
              decoration="underline"
              paddingLeft="10px"
              onClick={() =>
                updatePartnerTask({
                  partnerTaskId: task.id,
                  updates: {
                    status: PARTNER_TASK_STATUS_SENT,
                  },
                })
              }
            >
              Mark Not In Progress
            </SrText>
          )}
          {!isAnonymous && task.status === PARTNER_TASK_STATUS_SENT && (
            <SrText
              font="greycliff"
              cursor="pointer"
              weight="700"
              color="greyMonochrome"
              size="14"
              decoration="underline"
              paddingLeft="10px"
              onClick={() => setArchiveTaskModalOpen(true)}
            >
              Archive Task
            </SrText>
          )}
          <div>
            {showListingAddress && (
              <SrBox>
                <SrLink to={`/partner/${task.partner.slug}/listing/${task.listing.id}`}>
                  <SrText font="greycliff" cursor="pointer" weight="600" color="blue300" size="16">
                    {`${getListingFullAddressString(task.listing)}${
                      task.listing.daysOnMarket ? ` (${task.listing.daysOnMarket} DOM):` : ''
                    }`}
                  </SrText>
                </SrLink>
              </SrBox>
            )}
            {task.reviews.map((review) => {
              return (
                <div key={review.id}>
                  <SrText font="greycliff" size="16" weight="600">
                    {`"${getReviewFeedbackText(review)}"`}
                  </SrText>
                  <SrText font="greycliff" size="16" weight="400">
                    {` - Tour Feedback ${formatAgoDate(review.createdDate)}`}
                  </SrText>
                </div>
              );
            })}
            {task.reviews.length === 0 && task.category === PARTNER_TASK_CATEGORY_CLEAN && (
              <SrText font="greycliff" size="16" weight="400">
                {`It’s been ${pluralize(
                  'week',
                  getTaskWeeksAgo({
                    createdAt: task.createdAt,
                    addWeeks: 4,
                  }),
                  true,
                )} since the last cleaning task was completed.`}
              </SrText>
            )}
            {task.reviews.length === 0 && task.category === PARTNER_TASK_CATEGORY_LAWN && (
              <SrText font="greycliff" size="16" weight="400">
                {`It’s been ${pluralize(
                  'week',
                  getTaskWeeksAgo({
                    createdAt: task.createdAt,
                    addWeeks: 2,
                  }),
                  true,
                )} since the last lawn task was completed.`}
              </SrText>
            )}
            {task.reviews.length === 0 && isPriceReductionTask && task.keyword !== 'flatLined' && (
              <>
                <SrText font="greycliff" size="16" weight="600">
                  {`${
                    task.sunSpotPoint
                      ? `${formatPercentage(
                          task.sunSpotPoint,
                          false,
                        )} of SunSpot, low lead activity`
                      : 'Less leads per week'
                  } compared to similar listings. Try pricing the listing from`}
                </SrText>
                <SrText font="greycliff" size="16" weight="700" color="green">
                  {` ${formatPrice(task.listing.price)} → ${formatPrice(
                    task.priceDropRecommendation,
                  )}`}
                </SrText>
                <SrText font="greycliff" size="16" weight="600">
                  {!task.listing.underwrittenRent
                    ? '.'
                    : ` (underwritten ${formatPrice(task.listing.underwrittenRent)}).`}
                </SrText>
              </>
            )}
            {isPriceReductionTask && task.keyword === 'flatLined' && (
              <>
                <SrText font="greycliff" weight="600" size="16">
                  Listing received 0 leads over a 7 day period. Statistically speaking, this listing
                  has less than a 52% chance of getting leased if the price stays the same. Try
                  changing the listing from
                  <SrText font="greycliff" size="16" weight="700" color="green">
                    {` ${formatPrice(task.listing.price)} → ${formatPrice(
                      task.priceDropRecommendation,
                    )}`}
                    .
                  </SrText>
                </SrText>
              </>
            )}
            {task.status === PARTNER_TASK_STATUS_CANCELLED && (
              <SrBox paddingTop="15px">
                <SrText
                  display="block"
                  font="greycliff"
                  weight="700"
                  size="16"
                  color="greyMonochrome"
                >
                  {`Archive Reason${
                    task.cancelledAt
                      ? ` (${moment(task.cancelledAt).format('MMM D [at] h:mma')}):`
                      : ':'
                  }`}
                </SrText>
                <SrText font="greycliff" size="16" weight="600">
                  {!task.archiveReason
                    ? 'Archived automatically'
                    : `"${task.archiveReason}"${
                        task.archivedByUser ? ` - ${task.archivedByUser.name}` : ''
                      }`}
                </SrText>
              </SrBox>
            )}
          </div>
        </div>
        {!isAnonymous && (
          <SrTaskListRowAction
            task={task}
            updatePartnerTask={updatePartnerTask}
            onReducePrice={() => setPriceReductionModalOpen(true)}
          />
        )}
      </SrFlex>
      {priceReductionModalOpen && (
        <SrSimpleMessageModal
          iconUrl="/images/house-illustration2x.png"
          headline={`Lower the price to ${formatPrice(task.priceDropRecommendation)}`}
          message={
            'The price is updated across all internet listing sites automatically. ' +
            'It may take up to 24 hours to sync.'
          }
          maxWidth={375}
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          multiButtons={[
            {
              buttonText: 'Confirm',
              buttonTheme: BUTTON_THEME_GREEN_ADOBE,
              onButtonClick: onReducePrice,
            },
            {
              buttonText: 'Cancel',
              buttonTheme: BUTTON_THEME_GREY_ADOBE,
              onButtonClick: () => setPriceReductionModalOpen(false),
            },
          ]}
        />
      )}
      {archiveTaskModalOpen && (
        <SrDarkModal onClose={() => setArchiveTaskModalOpen(false)}>
          <Formik
            enableReinitialize
            initialValues={createInitialValues({ formFields: archiveTaskFormFields })}
            validate={createFormValidationFn(archiveTaskFormFields)}
            onSubmit={onArchiveTask}
            render={(formikBag) => {
              return (
                <Form>
                  <SrModalContent>
                    {archiveTaskFormFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                    <ButtonDeprecated
                      theme={BUTTON_THEME_GREEN_ADOBE}
                      type="submit"
                      margin="14px 0 0"
                    >
                      Archive Now
                    </ButtonDeprecated>
                  </SrModalContent>
                </Form>
              );
            }}
          />
        </SrDarkModal>
      )}
    </div>
  );
};

SrPartnerTaskListRow.propTypes = {
  task: PropTypes.object.isRequired,
  rowColorHex: PropTypes.string,
  borderColorHex: PropTypes.string,
  currentUser: PropTypes.object.isRequired,
  updatePartnerTask: PropTypes.func.isRequired,
  showListingAddress: PropTypes.bool,
};

SrPartnerTaskListRow.displayName = 'SrPartnerTaskListRow';

export default SrPartnerTaskListRow;
